import { create } from 'zustand';
import {
  AnalyticsFilterStore,
  AnalyticsFilterViewKey,
} from 'pages/ReportsPage/store/analyticsFilter.store.types';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import {
  DATE_PRESETS,
  DEFAULT_DATE_PRESET,
  getDefaultDateRange,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar/FilterBar.constants';

const createAnalyticsFilterStore = (view: AnalyticsFilterViewKey) =>
  create<AnalyticsFilterStore>()(
    devtoolsInNonProd(
      (set) => ({
        locationIds: null,
        employmentTypes: [],
        positions: null,
        dateRange: getDefaultDateRange(),
        datePreset: DEFAULT_DATE_PRESET,
        defaultPositionId: null,
        allPositionIds: null,
        payPeriodSelectionByLocation: {},

        setLocationIds: (ids) => set({ locationIds: ids }),
        setEmploymentTypes: (types) => set({ employmentTypes: types }),
        setPositions: (positions) => set({ positions }),
        setDateRange: (dateRange, preset) => set({ dateRange, datePreset: preset }),
        setDatePreset: (preset) => {
          const config = DATE_PRESETS.find((p) => p.key === preset);
          if (config) {
            set({ dateRange: config.getRange(), datePreset: preset });
          }
        },
        setDefaultPositionId: (id) => set({ defaultPositionId: id }),
        setAllPositionIds: (ids) =>
          set((state) =>
            state.allPositionIds !== null &&
            state.allPositionIds.length === ids.length &&
            state.allPositionIds.every((id, index) => id === ids[index])
              ? state
              : { allPositionIds: ids },
          ),
        rememberPayPeriodSelection: (locationId, selection) =>
          set((state) => ({
            payPeriodSelectionByLocation: {
              ...state.payPeriodSelectionByLocation,
              [locationId]: selection,
            },
          })),
        reset: () =>
          set({
            locationIds: null,
            employmentTypes: [],
            positions: null,
            dateRange: getDefaultDateRange(),
            datePreset: DEFAULT_DATE_PRESET,
            defaultPositionId: null,
            payPeriodSelectionByLocation: {},
          }),
      }),
      { name: `analyticsFilterStore/${view}` },
    ),
  );

export type AnalyticsFilterStoreInstance = ReturnType<typeof createAnalyticsFilterStore>;

const analyticsFilterStoresByView = new Map<AnalyticsFilterViewKey, AnalyticsFilterStoreInstance>();

export const getAnalyticsFilterStore = (
  view: AnalyticsFilterViewKey,
): AnalyticsFilterStoreInstance => {
  const existing = analyticsFilterStoresByView.get(view);
  if (existing) return existing;
  const store = createAnalyticsFilterStore(view);
  analyticsFilterStoresByView.set(view, store);
  return store;
};

export const resetAllAnalyticsFilterStores = () => {
  analyticsFilterStoresByView.forEach((store) => store.getState().reset());
};
